import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { MapPin, LogIn, LogOut, RefreshCw, Wifi, WifiOff, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HR_VIEW_ALL, fmtTime, fmtDur, fmtDate } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";
import { SelfieCapture } from "@/components/attendance/SelfieCapture";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import AttendanceAdmin from "@/components/attendance/AttendanceAdmin";

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}

export default function Attendance() {
  const { user, profile } = useAuth();
  const now = useClock();
  const [today, setToday] = useState(null);
  const [history, setHistory] = useState([]);
  const [gps, setGps] = useState(null);
  const [gpsErr, setGpsErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [selfie, setSelfie] = useState(null);

  const load = useCallback(() => {
    api.get("/attendance/me/today").then((r) => setToday(r.data)).catch(() => {});
    api.get("/attendance/me").then((r) => setHistory(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const getLocation = useCallback(() => {
    setGpsErr("");
    if (!navigator.geolocation) { setGpsErr("Perangkat tidak mendukung GPS"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: Math.round(pos.coords.accuracy) }),
      () => setGpsErr("Izin lokasi ditolak. Absensi akan tercatat tanpa lokasi."),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);
  useEffect(() => { getLocation(); }, [getLocation]);

  const doClock = async (type) => {
    setBusy(true);
    try {
      const body = {
        latitude: gps?.lat ?? null,
        longitude: gps?.lng ?? null,
        device_information: navigator.userAgent,
        clock_selfie: selfie,
      };
      const { data } = await api.post(`/attendance/${type}`, body);
      setToday(data);
      setSelfie(null);
      toast.success(type === "clock-in" ? "Clock In berhasil!" : "Clock Out berhasil!");
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const canOut = today?.clock_in_time && !today?.clock_out_time;
  const hasIn = !!today?.clock_in_time;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight lg:text-3xl">Absensi</h1>
        <p className="mt-1 text-sm text-slate-500">Lakukan clock in dan clock out harian Anda.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Clock widget */}
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="col-span-1 rounded-3xl border border-[#5a2427]/60 bg-gradient-to-br from-[#421517] via-[#4a1a1c] to-[#2a0d0e] p-6 text-white shadow-xl lg:col-span-2"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="text-sm text-slate-300">{profile?.full_name}</p>
              <p className="font-mono text-xs text-slate-400">
                {profile?.employee_id} · {profile?.department_name || "-"} · {profile?.position_name || "-"}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-400">{fmtDate(now)}</p>
              <p data-testid="live-clock" className="font-mono text-4xl font-bold tabular-nums tracking-tight">
                {now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </p>
            </div>
          </div>

          {/* GPS status */}
          <div className="mt-6 flex items-center gap-3 rounded-2xl bg-slate-950/40 p-4">
            <div className="relative">
              <MapPin className="h-5 w-5 text-sky-400" />
            </div>
            <div className="flex-1">
              {gps ? (
                <>
                  <p data-testid="gps-status-badge" className="flex items-center gap-1.5 text-sm font-medium text-emerald-400">
                    <Wifi className="h-3.5 w-3.5" /> Lokasi terdeteksi
                  </p>
                  <p className="font-mono text-[11px] text-slate-400">
                    {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)} · ±{gps.acc}m
                  </p>
                </>
              ) : (
                <p data-testid="gps-status-badge" className="flex items-center gap-1.5 text-sm font-medium text-amber-400">
                  <WifiOff className="h-3.5 w-3.5" /> {gpsErr || "Mendeteksi lokasi..."}
                </p>
              )}
            </div>
            <Button data-testid="gps-refresh-button" size="sm" variant="ghost"
              onClick={getLocation} className="text-slate-300 hover:text-white hover:bg-slate-800">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          {/* Selfie */}
          <div className="mt-4">
            <SelfieCapture value={selfie} onChange={setSelfie} />
          </div>

          {/* Buttons */}
          <div className="mt-6 grid grid-cols-2 gap-4">
            <motion.button
              whileTap={{ scale: 0.98 }} whileHover={{ scale: hasIn ? 1 : 1.02 }}
              data-testid="clock-in-button"
              disabled={hasIn || busy}
              onClick={() => doClock("clock-in")}
              className="flex flex-col items-center gap-2 rounded-2xl bg-emerald-500 py-6 font-semibold text-white transition-colors hover:bg-emerald-600 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
            >
              <LogIn className="h-6 w-6" />
              Clock In
              {today?.clock_in_time && <span className="font-mono text-xs">{fmtTime(today.clock_in_time)}</span>}
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.98 }} whileHover={{ scale: canOut ? 1.02 : 1 }}
              data-testid="clock-out-button"
              disabled={!canOut || busy}
              onClick={() => doClock("clock-out")}
              className="flex flex-col items-center gap-2 rounded-2xl bg-rose-500 py-6 font-semibold text-white transition-colors hover:bg-rose-600 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400"
            >
              <LogOut className="h-6 w-6" />
              Clock Out
              {today?.clock_out_time && <span className="font-mono text-xs">{fmtTime(today.clock_out_time)}</span>}
            </motion.button>
          </div>
        </motion.div>

        {/* Today status */}
        <Card className="col-span-1 rounded-2xl border-slate-200 p-6 dark:border-slate-800">
          <div className="mb-4 flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            <h3 className="font-heading text-lg font-semibold tracking-tight">Status Hari Ini</h3>
          </div>
          {today ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Status</span>
                <StatusBadge status={today.attendance_status} testid="today-status-badge" />
              </div>
              <Row label="Clock In" value={fmtTime(today.clock_in_time)} />
              <Row label="Clock Out" value={fmtTime(today.clock_out_time)} />
              <Row label="Durasi Kerja" value={fmtDur(today.work_duration)} />
              <Row label="Terlambat" value={`${today.late_minutes || 0} menit`} />
              <Row label="Lokasi" value={today.location || "-"} />
              {today.distance_m != null && <Row label="Jarak Kantor" value={`${today.distance_m} m`} />}
              {(today.clock_in_selfie || today.clock_out_selfie) && (
                <div className="flex gap-3 pt-1">
                  {today.clock_in_selfie && (
                    <div className="text-center">
                      <img src={today.clock_in_selfie} alt="selfie masuk" className="h-16 w-16 rounded-lg object-cover" data-testid="today-selfie-in" />
                      <p className="mt-1 text-[10px] text-slate-400">Masuk</p>
                    </div>
                  )}
                  {today.clock_out_selfie && (
                    <div className="text-center">
                      <img src={today.clock_out_selfie} alt="selfie keluar" className="h-16 w-16 rounded-lg object-cover" data-testid="today-selfie-out" />
                      <p className="mt-1 text-[10px] text-slate-400">Keluar</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-slate-400">Anda belum melakukan absensi hari ini.</p>
          )}
        </Card>
      </div>

      {/* Personal history */}
      <Card className="rounded-2xl border-slate-200 dark:border-slate-800">
        <div className="border-b border-slate-100 px-6 py-4 dark:border-slate-800">
          <h3 className="font-heading text-lg font-semibold tracking-tight">Riwayat Absensi Saya</h3>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Tanggal</TableHead>
                <TableHead>Masuk</TableHead>
                <TableHead>Keluar</TableHead>
                <TableHead>Durasi</TableHead>
                <TableHead>Terlambat</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-8 text-center text-slate-400">Belum ada data.</TableCell></TableRow>
              )}
              {history.map((h) => (
                <TableRow key={h.id} data-testid="attendance-history-row">
                  <TableCell className="font-medium">{fmtDate(h.date)}</TableCell>
                  <TableCell className="font-mono text-sm">{fmtTime(h.clock_in_time)}</TableCell>
                  <TableCell className="font-mono text-sm">{fmtTime(h.clock_out_time)}</TableCell>
                  <TableCell className="font-mono text-sm">{fmtDur(h.work_duration)}</TableCell>
                  <TableCell className="font-mono text-sm">{h.late_minutes || 0}m</TableCell>
                  <TableCell><StatusBadge status={h.attendance_status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Admin/Supervisor all-attendance view */}
      {[...HR_VIEW_ALL, "SUPERVISOR"].includes(user.role) && <AttendanceAdmin role={user.role} />}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="font-mono text-sm font-medium">{value}</span>
    </div>
  );
}
